import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/apiService';
import { encodeId } from '../utils/urlSecurity';
import bmailogo from '../assets/bmailogo.png';

const surveyQuestions = [
  // Section 1: Technical Feasibility (Questions 1-4)
  {
    id: 1,
    section: "Technical Feasibility Assessment",
    title: "Process Automation Potential",
    description: "How much of this process can be automated based on clear rules/patterns vs. requiring human judgment?",
    options: [
      { value: 10, label: "+10: Can be fully automated with clear rules/patterns" },
      { value: 8, label: "+8" },
      { value: 6, label: "+6: Can be automated with minor human oversight needed" },
      { value: 4, label: "+4" },
      { value: 2, label: "+2: Can be automated with some human oversight needed" },
      { value: 0, label: "0: Balanced mix of human and AI decisions" },
      { value: -2, label: "-2: Can be automated but heavy human judgment required" },
      { value: -4, label: "-4" },
      { value: -6, label: "-6: Almost entirely human-dependent" },
      { value: -8, label: "-8" },
      { value: -10, label: "-10: Requires human input throughout the process" }
    ]
  },
  {
    id: 2,
    section: "Technical Feasibility Assessment",
    title: "Infrastructure Requirements",
    description: "What are your technology deployment constraints?",
    options: [
      { value: 10, label: "+10: Fully open to public cloud-based AI services" },
      { value: 8, label: "+8" },
      { value: 6, label: "+6: Open to public or private cloud, some data residency requirements" },
      { value: 4, label: "+4" },
      { value: 2, label: "+2: Limited cloud usage, some data residency requirements" },
      { value: 0, label: "0: Balanced hybrid requirements, case-by-case evaluation" },
      { value: -2, label: "-2: Strong on-premise preference, minimal cloud usage" },
      { value: -4, label: "-4" },
      { value: -6, label: "-6: Strict on-premise requirements, limited external services" },
      { value: -8, label: "-8" },
      { value: -10, label: "-10: Must be 100% on-premise, no external connectivity" }
    ]
  },
  {
    id: 3,
    section: "Technical Feasibility Assessment",
    title: "System Integration Complexity",
    description: "How accessible are the required system integrations?",
    options: [
      { value: 10, label: "+10: All systems have comprehensive REST APIs and MCP layers" },
      { value: 8, label: "+8" },
      { value: 6, label: "+6: Most systems have documented REST APIs, minor research needed" },
      { value: 4, label: "+4" },
      { value: 2, label: "+2: Most systems have base REST APIs, may need minor customization" },
      { value: 0, label: "0: Balanced effort with some APIs and some custom work required" },
      { value: -2, label: "-2: Few REST APIs available, mostly manual data exchange needed" },
      { value: -4, label: "-4" },
      { value: -6, label: "-6: Limited or older interfaces (SOAP, EDI), custom work required" },
      { value: -8, label: "-8" },
      { value: -10, label: "-10: No APIs available, major customization needed" }
    ]
  },
  {
    id: 4,
    section: "Technical Feasibility Assessment",
    title: "Documentation and Requirements Clarity",
    description: "How well-defined is the current process?",
    options: [
      { value: 10, label: "+10: Comprehensive documentation, fully standardized" },
      { value: 8, label: "+8" },
      { value: 6, label: "+6: Good documentation exists, minor requirements gaps" },
      { value: 4, label: "+4" },
      { value: 2, label: "+2: Adequate documentation, some requirements need clarification" },
      { value: 0, label: "0: Basic documentation, moderate requirements gathering needed" },
      { value: -2, label: "-2: Poor documentation, extensive requirements gathering needed" },
      { value: -4, label: "-4" },
      { value: -6, label: "-6: Very limited documentation, major process analysis required" },
      { value: -8, label: "-8" },
      { value: -10, label: "-10: No documentation exists, process completely undefined" }
    ]
  },
  // Section 2: ROI Potential (Questions 5-7)
  {
    id: 5,
    section: "ROI Potential Assessment",
    title: "Time Spent on This Process",
    description: "How much time do people currently spend completing this process?",
    options: [
      { value: 10, label: "+10: Extremely time-consuming" },
      { value: 8, label: "+8" },
      { value: 6, label: "+6: High amount of time required" },
      { value: 4, label: "+4" },
      { value: 2, label: "+2: Noticeable amount of time required" },
      { value: 0, label: "0: Moderate amount of time required" },
      { value: -2, label: "-2: Some time required" },
      { value: -4, label: "-4" },
      { value: -6, label: "-6: Small amount of time required" },
      { value: -8, label: "-8" },
      { value: -10, label: "-10: No time required; already automated or negligible effort" }
    ]
  },
  {
    id: 6,
    section: "ROI Potential Assessment",
    title: "Number of People Involved in This Process",
    description: "How many people or teams are usually involved in completing this process?",
    options: [
      { value: 10, label: "+10: Large number of people and heavy coordination required" },
      { value: 8, label: "+8" },
      { value: 6, label: "+6: Multiple teams involved" },
      { value: 4, label: "+4" },
      { value: 2, label: "+2: Several people involved" },
      { value: 0, label: "0: Moderate number of people involved" },
      { value: -2, label: "-2: Limited involvement across people" },
      { value: -4, label: "-4" },
      { value: -6, label: "-6: One primary person with occasional help" },
      { value: -8, label: "-8" },
      { value: -10, label: "-10: One person only; no coordination needed" }
    ]
  },
  {
    id: 7,
    section: "ROI Potential Assessment",
    title: "Consistency of Results from This Process",
    description: "How consistent are the results or data produced by this process today?",
    options: [
      { value: 10, label: "+10: Highly inconsistent or error-prone results" },
      { value: 8, label: "+8" },
      { value: 6, label: "+6: High inconsistency requiring corrections" },
      { value: 4, label: "+4" },
      { value: 2, label: "+2: Noticeable inconsistency" },
      { value: 0, label: "0: Moderately consistent" },
      { value: -2, label: "-2: Some inconsistency occurs" },
      { value: -4, label: "-4" },
      { value: -6, label: "-6: Mostly consistent with rare variation" },
      { value: -8, label: "-8" },
      { value: -10, label: "-10: Always consistent and standardized" }
    ]
  },
  // Section 3: Strategic Value (Questions 8-10)
  {
    id: 8,
    section: "Strategic Value Assessment",
    title: "Board-Level Alignment",
    description: "Does this project align with board-level strategic initiatives?",
    options: [
      { value: 10, label: "10: Direct alignment with multiple critical board priorities" },
      { value: 9, label: "9" },
      { value: 8, label: "8: Clear alignment with major board objective" },
      { value: 7, label: "7" },
      { value: 6, label: "6: Good alignment with important board priority" },
      { value: 5, label: "5: Some alignment with board priorities" },
      { value: 4, label: "4" },
      { value: 3, label: "3: Minimal connection to board priorities" },
      { value: 2, label: "2" },
      { value: 1, label: "1: No clear board-level alignment" }
    ]
  },
  {
    id: 9,
    section: "Strategic Value Assessment",
    title: "Brand Impact",
    description: "Would this project positively impact your organization's brand?",
    options: [
      { value: 10, label: "10: Transformational brand impact, major competitive advantage" },
      { value: 9, label: "9" },
      { value: 8, label: "8: Significant positive brand impact, strong differentiation" },
      { value: 7, label: "7" },
      { value: 6, label: "6: Positive brand effect, moderate differentiation" },
      { value: 5, label: "5: Some positive brand effect, minor differentiation" },
      { value: 4, label: "4" },
      { value: 3, label: "3: Limited brand impact" },
      { value: 2, label: "2" },
      { value: 1, label: "1: No meaningful brand impact" }
    ]
  },
  {
    id: 10,
    section: "Strategic Value Assessment",
    title: "Organizational Goal Alignment",
    description: "How well does this align with departmental/organizational goals?",
    options: [
      { value: 10, label: "10: Perfect alignment with multiple strategic goals across organization" },
      { value: 9, label: "9" },
      { value: 8, label: "8: Strong alignment with major organizational goal(s)" },
      { value: 7, label: "7" },
      { value: 6, label: "6: Moderate alignment with organizational goal(s)" },
      { value: 5, label: "5: Some alignment with departmental goal(s)" },
      { value: 4, label: "4" },
      { value: 3, label: "3: Limited alignment with current team objectives" },
      { value: 2, label: "2" },
      { value: 1, label: "1: Minimal to no clear alignment with current objectives" }
    ]
  }
];

const SurveyForm = () => {
  const [formData, setFormData] = useState({
    email: '',
    opportunity_description: '',
    questions: {}
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const navigate = useNavigate();

  const sections = [
    { id: 1, name: "Technical Feasibility Assessment", questions: [1, 2, 3, 4] },
    { id: 2, name: "ROI Potential Assessment", questions: [5, 6, 7] },
    { id: 3, name: "Strategic Value Assessment", questions: [8, 9, 10] }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Real-time validation for opportunity description (only if submit was attempted)
    if (field === 'opportunity_description' && submitAttempted) {
      const descriptionError = validateDescriptionContent(value);
      setErrors(prev => ({
        ...prev,
        [field]: descriptionError || ''
      }));
    } else {
      // Clear error when user starts typing in other fields
      if (errors[field]) {
        setErrors(prev => ({
          ...prev,
          [field]: ''
        }));
      }
    }
    
    // Clear submit attempted flag when user makes changes after failed submission
    if (submitAttempted && errors[field]) {
      // Check if all errors are now resolved
      const remainingErrors = Object.keys(errors).filter(key => {
        if (key === field) return false; // This field is being fixed
        return errors[key]; // Check if other errors exist
      });
      
      if (remainingErrors.length === 0) {
        setSubmitAttempted(false);
      }
    }
  };

  const handleQuestionChange = (questionId, answer) => {
    setFormData(prev => ({
      ...prev,
      questions: {
        ...prev.questions,
        [questionId]: parseInt(answer)
      }
    }));
    // Clear error when user selects answer
    if (errors[`question_${questionId}`]) {
      setErrors(prev => ({
        ...prev,
        [`question_${questionId}`]: ''
      }));
      
      // Clear submit attempted flag if all errors are now resolved
      if (submitAttempted) {
        const remainingErrors = Object.keys(errors).filter(key => {
          if (key === `question_${questionId}`) return false;
          return errors[key];
        });
        
        if (remainingErrors.length === 0) {
          setSubmitAttempted(false);
        }
      }
    }
  };





  const validateDescriptionContent = (description) => {
    if (!description || description.trim().length < 15) {
      return 'Please provide a meaningful description of at least 15 characters explaining your AI opportunity';
    }

    const cleanText = description.trim().toLowerCase();
    
    // Check for common test/gibberish patterns
    const gibberishPatterns = [
      'test', 'asdf', 'qwerty', 'hello world', 'sample text', 'lorem ipsum', 
      'gibberish', 'random text', 'blah', 'placeholder', 'example', 'sample', 
      'demo', 'testing', 'aaa', 'bbb', 'ccc', 'ddd', 'eee'
    ];
    
    const words = cleanText.split(/\s+/);
    if (words.length === 0) {
      return 'Please provide a meaningful description of your AI opportunity';
    }

    const gibberishWordCount = words.filter(word => 
      gibberishPatterns.some(pattern => word.includes(pattern))
    ).length;

    // Check if description is mostly gibberish patterns
    if (words.length <= 5 && gibberishWordCount >= words.length * 0.4) {
      return 'Please provide a meaningful description of your AI opportunity instead of test data or placeholder text';
    } else if (words.length > 5 && gibberishWordCount >= words.length * 0.6) {
      return 'Please provide a meaningful description of your AI opportunity instead of test data or placeholder text';
    }

    // Check for excessive character repetition
    const uniqueChars = new Set(cleanText.replace(/\s/g, '')).size;
    if (cleanText.length > 20 && uniqueChars < 8) {
      return 'Please provide a meaningful description using varied words and characters instead of repeated characters';
    }

    // Check for keyboard mashing patterns
    const keyboardSequences = ['qwertyuiop', 'asdfghjkl', 'zxcvbnm', '1234567890'];
    const textNoSpaces = cleanText.replace(/\s/g, '');
    for (const seq of keyboardSequences) {
      if (textNoSpaces.length > 5) {
        // Check for sequences of 5+ consecutive keyboard characters to avoid false positives
        for (let i = 0; i <= seq.length - 5; i++) {
          if (textNoSpaces.includes(seq.substring(i, i + 5))) {
            return 'Please provide a meaningful description of your AI opportunity instead of random keystrokes';
          }
        }
      }
    }

    // Check for single repeated words
    if (words.length >= 3) {
      const wordCounts = {};
      words.forEach(word => {
        if (word.length > 2) {
          wordCounts[word] = (wordCounts[word] || 0) + 1;
        }
      });

      const meaningfulWords = words.filter(w => w.length > 2);
      if (meaningfulWords.length > 0) {
        const maxWordCount = Math.max(...Object.values(wordCounts));
        if (maxWordCount / meaningfulWords.length > 0.6) {
          return 'Please provide a meaningful and varied description of your AI opportunity instead of repeating the same words';
        }
      }
    }

    return null; // No validation errors
  };

  const validateForm = () => {
    const newErrors = {};

    // Validate basic fields
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.opportunity_description) {
      newErrors.opportunity_description = 'Opportunity description is required';
    } else {
      const descriptionError = validateDescriptionContent(formData.opportunity_description);
      if (descriptionError) {
        newErrors.opportunity_description = descriptionError;
      }
    }

    // Validate all questions are answered
    for (let i = 1; i <= 10; i++) {
      if (formData.questions[i] === undefined || formData.questions[i] === null) {
        newErrors[`question_${i}`] = 'This question is required';
      }
    }

    // Only set errors if there are any
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return false;
    }
    
    // Clear errors if validation passes
    setErrors({});
    return true;
  };

  const calculateProgress = () => {
    const totalFields = 12; // 2 basic fields + 10 questions (key not included in progress)
    let completed = 0;
    
    if (formData.email) completed++;
    if (formData.opportunity_description) completed++;
    
    for (let i = 1; i <= 10; i++) {
      if (formData.questions[i] !== undefined) completed++;
    }

    return Math.round((completed / totalFields) * 100);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setSubmitAttempted(true);
    
    if (!validateForm()) {
      // Scroll to first error
      const firstError = document.querySelector('.error-message');
      if (firstError) {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsSubmitting(true);

    try {
      // Convert questions object to the format expected by backend
      const submissionData = {
        email: formData.email,
        opportunity_description: formData.opportunity_description,
        // Convert questions to individual fields
        question_1: formData.questions[1],
        question_2: formData.questions[2],
        question_3: formData.questions[3],
        question_4: formData.questions[4],
        question_5: formData.questions[5],
        question_6: formData.questions[6],
        question_7: formData.questions[7],
        question_8: formData.questions[8],
        question_9: formData.questions[9],
        question_10: formData.questions[10]
      };

      // Submit the survey using apiService
      const response = await apiService.submitSurvey(submissionData);
      
      if (response && response.submission_id) {
        
        // To prevent any possible race conditions or redirects causing a GET request
        // We'll add a small delay before navigation
        setTimeout(() => {
          // Navigate to submitted page with the response data and encoded ID
          const encodedId = encodeId(response.submission_id);
          navigate(`/submitted/${encodedId}`, { 
            state: { 
              surveyData: response,
              formData: formData
            },
            replace: true // Use replace instead of push to avoid browser back button issues
          });
        }, 100);
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      let errorMessage = 'Failed to submit survey. Please try again.';
      
      if (error.response) {
        // Server responded with error status
        errorMessage = error.response?.data?.detail || `Server error: ${error.response.status}`;
      } else if (error.request) {
        // Request was made but no response received
        errorMessage = 'Unable to connect to server. Please check your connection and try again.';
      } else {
        // Something else happened
        errorMessage = error.message || 'An unexpected error occurred.';
      }
      
      setErrors({ 
        submit: errorMessage
      });
      
      // Scroll to error message
      setTimeout(() => {
        const errorElement = document.querySelector('.error-message, [class*="error"]');
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Remove currentSectionData - no longer needed
  const progress = calculateProgress();

  return (
    <div className="max-w-7xl mx-auto p-5">
      <div className="flex flex-col md:flex-row">
        <div className="md:w-3/4 md:pr-6">
          {/* Header */}
          <div className="text-center text-white mb-8">
            {/* Logo and Admin Login on same row */}
            <div className="flex justify-between items-center mb-6">
              <img 
                src={bmailogo} 
                alt="ByteMethod Logo" 
                style={{ maxWidth: '280px', height: 'auto' }}
              />
              <button
                type="button"
                onClick={() => navigate('/admin')}
                className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg font-medium transition-all duration-200 hover:scale-105 backdrop-blur-sm border border-white/30 flex items-center gap-2"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0010 11z" clipRule="evenodd" />
                </svg>
                Admin Login
              </button>
            </div>
            
            <h1 className="text-3xl md:text-4xl font-semibold mb-3 drop-shadow-lg">
              Opportunity Assessment Survey
            </h1>
            <p className="text-lg md:text-xl opacity-90">
              Evaluate the potential of your AI initiatives
            </p>
          </div>


        </div>
        
        {/* Progress Widget - Now on the right side */}
        <div className="md:w-1/4 mb-6 md:mb-0">
          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-5 md:fixed md:right-12 md:top-1/2 md:transform md:-translate-y-1/2 md:w-72">
            <div className="flex flex-col items-center">
              {/* Progress Circle */}
              <div className="relative w-32 h-32 mb-4">
                {/* Background Circle */}
                <svg className="w-full h-full" viewBox="0 0 100 100">
                  <circle 
                    cx="50" 
                    cy="50" 
                    r="45" 
                    fill="transparent" 
                    stroke="rgba(255,255,255,0.1)" 
                    strokeWidth="8"
                  />
                  {/* Progress Circle - Only show if there's progress */}
                  {progress > 0 && (
                    <circle 
                      cx="50" 
                      cy="50" 
                      r="45"
                      fill="transparent" 
                      stroke="url(#blue-to-purple-gradient)" 
                      strokeWidth="8"
                      strokeDasharray={`${2 * Math.PI * 45 * progress / 100} ${2 * Math.PI * 45 * (1 - progress / 100)}`}
                      strokeDashoffset={2 * Math.PI * 45 * 0.25}
                      strokeLinecap="round"
                      transform="rotate(-90 50 50)"
                    />
                  )}
                  {/* Define the gradient */}
                  <defs>
                    <linearGradient id="blue-to-purple-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#8B5CF6" /> {/* violet-500 */}
                      <stop offset="100%" stopColor="#C084FC" /> {/* purple-400 */}
                    </linearGradient>
                  </defs>
                </svg>
                {/* Centered Text */}
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-2xl font-bold text-[#8B5CF6]">{progress}%</span>
                  <span className="text-sm text-gray-400">Complete</span>
                </div>
              </div>
              
              {/* Assessment Progress Section */}
              <div className="w-full text-white">
                <h3 className="text-center font-medium text-lg mb-4">Assessment Progress</h3>
                
                {/* Fields Completed */}
                <div className="bg-white/5 border border-white/10 rounded-lg p-3 mb-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-300">Fields Completed</span>
                    <span className="font-medium">
                      {(formData.email ? 1 : 0) + 
                      (formData.opportunity_description ? 1 : 0) + 
                      Object.keys(formData.questions).length} / 12
                    </span>
                  </div>
                </div>
                
                {/* Questions Answered */}
                <div className="bg-white/5 border border-white/10 rounded-lg p-3 mb-6">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-300">Questions Answered</span>
                    <span className="font-medium">{Object.keys(formData.questions).length} / 10</span>
                  </div>
                </div>
                
                {/* Divider */}
                <div className="border-t border-gray-700 mb-4"></div>
                
                {/* Assessment Matrix */}
                <h3 className="text-center font-medium text-lg mb-3">Assessment Matrix</h3>
                <div className="space-y-3">
                  {/* Technical Feasibility */}
                  <div className="bg-[#111111] border border-blue-900 rounded-lg p-3">
                    <div className="flex justify-between items-center">
                      <span className="text-blue-300">Technical Feasibility</span>
                      <span className="text-blue-300">-10 to +10</span>
                    </div>
                  </div>
                  
                  {/* ROI Potential */}
                  <div className="bg-[#111111] border border-green-900 rounded-lg p-3">
                    <div className="flex justify-between items-center">
                      <span className="text-green-300">ROI Potential</span>
                      <span className="text-green-300">-10 to +10</span>
                    </div>
                  </div>
                  
                  {/* Strategic Value */}
                  <div className="bg-[#111111] border border-orange-900 rounded-lg p-3">
                    <div className="flex justify-between items-center">
                      <span className="text-orange-300">Strategic Value</span>
                      <span className="text-orange-300">1 to 10</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Survey Form */}
      <form onSubmit={handleSubmit} className="md:w-3/4 md:pr-6" method="post" noValidate>
        {/* Basic Information */}
        <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-8 mb-6">
          <h2 className="text-2xl font-bold text-white mb-6">Your AI Opportunity</h2>
          <p className="text-gray-300 mb-6">Describe your AI opportunity idea. We'll automatically generate a descriptive name for your submission.</p>
          
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Email Address *
              </label>
              <input
                type="email"
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="your.email@company.com"
              />
              {submitAttempted && errors.email && errors.email.trim() !== '' && (
                <p className="mt-2 text-sm text-red-300">{errors.email}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Opportunity Description *
              </label>
              <textarea
                rows="4"
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 resize-none"
                value={formData.opportunity_description}
                onChange={(e) => handleInputChange('opportunity_description', e.target.value)}
                placeholder="Describe the AI opportunity, current process, and expected outcomes..."
              />
              {submitAttempted && errors.opportunity_description && errors.opportunity_description.trim() !== '' && (
                <p className="mt-2 text-sm text-red-300">{errors.opportunity_description}</p>
              )}
            </div>
          </div>
        </div>

        {/* All Sections - Scrollable */}
        {sections.map(section => (
          <div key={section.id} className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-8 mb-6">
            <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white p-4 rounded-lg mb-6">
              <h3 className="text-xl font-semibold">{section.name}</h3>
            </div>

            <div className="space-y-8">
              {surveyQuestions
                .filter(q => section.questions.includes(q.id))
                .map(question => (
                <div key={question.id} className="bg-white/5 border border-white/10 rounded-lg p-6">
                  <div className="text-lg font-semibold text-white mb-3">
                    {question.id}. {question.title}
                  </div>
                  <p className="text-gray-200 mb-4">
                    {question.description}
                  </p>

                  <div className="relative">
                    <select
                      className="w-full px-4 py-4 pr-12 border-2 border-white/30 rounded-xl bg-gray-900/90 backdrop-blur-sm text-white placeholder-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:bg-gray-800/90 transition-all duration-200 appearance-none cursor-pointer hover:border-white/50 hover:bg-gray-800/90"
                      value={formData.questions[question.id] !== undefined ? formData.questions[question.id] : ''}
                      onChange={(e) => handleQuestionChange(question.id, e.target.value)}
                    >
                      <option value="" disabled className="bg-gray-900 text-gray-400">
                        Select an option...
                      </option>
                      {question.options.map(option => (
                        <option 
                          key={option.value} 
                          value={option.value} 
                          className="bg-gray-900 text-white py-3 px-4 hover:bg-gray-700 cursor-pointer"
                          style={{
                            backgroundColor: '#111827',
                            color: '#ffffff',
                            padding: '12px 16px',
                          }}
                        >
                          {option.label}
                        </option>
                      ))}
                    </select>
                    {/* Custom dropdown arrow overlay for better styling */}
                    <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
                      <svg 
                        className="h-5 w-5 text-white/80 transition-colors duration-200" 
                        xmlns="http://www.w3.org/2000/svg" 
                        viewBox="0 0 20 20" 
                        fill="currentColor"
                      >
                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </div>

                  {submitAttempted && errors[`question_${question.id}`] && errors[`question_${question.id}`].trim() !== '' && (
                    <p className="mt-3 text-sm text-red-300">{errors[`question_${question.id}`]}</p>
                  )}
                </div>
                ))}
            </div>
          </div>
        ))}

        {/* Submit Button */}
        <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-6">
          <div className="flex justify-center">
            <button
              type="submit"
              disabled={isSubmitting}
              className={`px-8 py-3 rounded-lg font-medium transition-all duration-200 ${
                isSubmitting 
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-gradient-to-r from-blue-500 to-purple-600 hover:shadow-lg hover:scale-105'
              } text-white`}
            >
              {isSubmitting ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Submitting...
                </span>
              ) : (
                'Submit Assessment'
              )}
            </button>
          </div>
        </div>
        </form>
    </div>
  );
};

export default SurveyForm;